// ConsultantContext — global state for the AI Cattle Consultant.
//
// One active conversation per session. State persists across page navigation
// because the provider is mounted at the Layout level, above the router Outlet.
// Drawer open/close is also global so any component can trigger the consultant
// (e.g. a Cmd+K command bar in the future, or deep links from other tools).

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ProvenanceDrawerProvider } from "@/components/consultant/ProvenanceDrawer";
import { supabase } from "@/integrations/supabase/client";
import type { ConsultantMessage, MessagePart } from "@/types/consultant";
import { appendTextDelta, shouldKeepStreamedPart } from "@/lib/consultantStreamParts";

interface ConsultantContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;

  activeConversationId: string | null;
  messages: ConsultantMessage[];
  isPending: boolean;
  // Name of the currently-running tool (from the most recent tool_start
  // event without a matching completion). Lets the UI show "Pulling sale
  // barn data…" instead of a generic spinner while tools execute.
  runningTool: string | null;
  lastError: string | null;
  premiumRequired: boolean;

  sendMessage: (text: string) => Promise<void>;
  /** Halt the answer in flight, keeping whatever already streamed. */
  stopGeneration: () => void;
  /** Ask the last question again. No-op while an answer is running. */
  regenerate: () => void;
  /** Whether there is a question to ask again. */
  canRegenerate: boolean;
  loadConversation: (conversationId: string) => Promise<void>;
  newConversation: () => void;
}

const ConsultantContext = createContext<ConsultantContextValue | null>(null);

export function ConsultantProvider({ children }: { children: ReactNode }) {
  // Holds the in-flight request so stopGeneration can reach it. A ref, not
  // state: the UI never reads it, and re-rendering the chat mid-stream to
  // store a controller would be a needless cost on every send.
  const abortRef = useRef<AbortController | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(
    null,
  );
  const [messages, setMessages] = useState<ConsultantMessage[]>([]);
  const [isPending, setIsPending] = useState(false);
  const [runningTool, setRunningTool] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [premiumRequired, setPremiumRequired] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isPending) return;

      // Optimistic user message — appears immediately.
      const optimisticUserId = `optimistic-${Date.now()}`;
      const optimisticUser: ConsultantMessage = {
        id: optimisticUserId,
        conversation_id: activeConversationId ?? "pending",
        role: "user",
        content: [{ type: "text", content: trimmed }],
        status: "complete",
        created_at: new Date().toISOString(),
      };

      // Streaming placeholder assistant message — gets populated via SSE
      // events. Its id is provisional until the "conversation" event arrives
      // with the real server-side message id.
      const streamingPlaceholderId = `streaming-${Date.now()}`;
      const streamingMsg: ConsultantMessage = {
        id: streamingPlaceholderId,
        conversation_id: activeConversationId ?? "streaming",
        role: "assistant",
        content: [],
        status: "streaming",
        created_at: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, optimisticUser, streamingMsg]);
      setIsPending(true);
      setLastError(null);

      // ⚠️ ONE CONTROLLER PER SEND, HELD IN A REF SO stopGeneration CAN REACH
      // IT. Kept out of state deliberately: a state update would re-render the
      // whole chat mid-stream, and the controller is not something the UI reads.
      const controller = new AbortController();
      abortRef.current = controller;

      // Tracks the target message id for state updates — starts as the
      // placeholder, swaps to the real one when the conversation event
      // arrives. Captured by the state-update closures below.
      let currentMessageId = streamingPlaceholderId;
      let errorFromStream: string | null = null;
      // Set when the server sends the clean "done" event. If the streaming
      // connection then drops on its close (iOS reports this as "Load failed"),
      // we know the answer was already complete and ignore that read error.
      let doneReceived = false;

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) throw new Error("Not authenticated");

        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
        const SUPABASE_ANON_KEY = import.meta.env
          .VITE_SUPABASE_PUBLISHABLE_KEY as string;

        // ⚠️ DEFAULTS TO PROD. The override exists so a branch can be exercised
        // against cattle-consultant-staging — same database, same data, only the
        // deployed function differs — without hand-editing this line and hoping
        // someone remembers to revert it. Set VITE_CONSULTANT_FN in .env.local
        // (which is gitignored); an unset or empty value is always prod.
        const CONSULTANT_FN =
          (import.meta.env.VITE_CONSULTANT_FN as string | undefined)?.trim() ||
          "cattle-consultant";

        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/${CONSULTANT_FN}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              apikey: SUPABASE_ANON_KEY,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              conversation_id: activeConversationId,
              message: trimmed,
            }),
            signal: controller.signal,
          },
        );

        const contentType = response.headers.get("Content-Type") ?? "";
        const isSSE = contentType.includes("text/event-stream");

        // Non-streaming error path: server returned JSON with an error
        if (!response.ok || !isSSE) {
          const body = await response.json().catch(() => ({}));
          if (body.error === "premium_required") {
            setPremiumRequired(true);
            throw new Error(
              body.message ?? "The AI Cattle Consultant is a premium feature.",
            );
          }
          throw new Error(body.error ?? `HTTP ${response.status}`);
        }

        if (!response.body) throw new Error("No response body");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        // Consume the stream chunk by chunk, parsing SSE events.
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // Events are separated by a blank line (\n\n).
          let eolIdx;
          while ((eolIdx = buffer.indexOf("\n\n")) !== -1) {
            const eventStr = buffer.slice(0, eolIdx);
            buffer = buffer.slice(eolIdx + 2);

            // Keepalive comments (: keepalive) — skip.
            if (!eventStr.trim() || eventStr.startsWith(":")) continue;

            // Parse "event: X\ndata: Y" form. Data may span multiple
            // data: lines per the SSE spec (rare but permitted).
            let eventType = "";
            let dataStr = "";
            for (const line of eventStr.split("\n")) {
              if (line.startsWith("event: ")) eventType = line.slice(7).trim();
              else if (line.startsWith("data: ")) dataStr += line.slice(6);
            }
            if (!dataStr) continue;

            let data: Record<string, unknown>;
            try {
              data = JSON.parse(dataStr);
            } catch {
              continue;
            }

            switch (eventType) {
              case "conversation": {
                const newMessageId = String(data.message_id ?? "");
                const newConversationId = String(data.conversation_id ?? "");
                currentMessageId = newMessageId;
                setActiveConversationId(newConversationId);
                setMessages((prev) =>
                  prev.map((m) => {
                    if (m.id === streamingPlaceholderId) {
                      return {
                        ...m,
                        id: newMessageId,
                        conversation_id: newConversationId,
                      };
                    }
                    if (m.id === optimisticUserId) {
                      return { ...m, conversation_id: newConversationId };
                    }
                    return m;
                  }),
                );
                break;
              }

              case "text": {
                // Append delta to the last text part if one exists at the
                // end; otherwise start a new text part. This naturally
                // handles the "text → tool result → more text" pattern
                // because after a non-text part, a text delta creates a
                // fresh text part.
                const delta = String(data.delta ?? "");
                if (!delta) break;
                // Clear tool indicator — Claude is now writing text.
                setRunningTool(null);
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === currentMessageId
                      ? { ...m, content: appendTextDelta(m.content, delta) }
                      : m,
                  ),
                );
                break;
              }

              case "tool_start": {
                // Show a tool-specific progress indicator while the tool
                // executes server-side (can take 500ms–3s for DB-heavy RPCs).
                // Without this the user sees "Analyzing the data…" for the
                // whole execution window even though things are happening.
                const toolName = String(data.tool_name ?? "");
                if (toolName) setRunningTool(toolName);
                break;
              }

              case "part": {
                // Completed MessagePart. Backend also sends text parts for
                // finalized text blocks, but we've already built the text
                // via "text" deltas — skip duplicate text parts.
                const part = data.part as MessagePart | undefined;
                if (!part) break;
                // Clear the running-tool indicator — a part means a tool
                // produced output OR Claude finished a text block.
                setRunningTool(null);
                // Drop DUPLICATE prose text parts — the prose already arrived
                // via "text" deltas, so keeping the finalized block too would
                // print the whole answer twice.
                //
                // But CITATION-ONLY parts are also type "text": tools that
                // synthesize prose instead of a table attach their source as an
                // empty-content text part carrying `provenance` (see
                // MessagePart in src/types/consultant.ts and the citation-only
                // parts emitted across the consultant tool handlers). Those
                // carry no prose to duplicate, and blanket-skipping "text"
                // silently discarded every one of them — so provenance chips
                // never rendered during a live stream, even though the parts
                // were persisted correctly to consultant_messages and DID show
                // after a page reload (which reads from the DB, not the
                // stream). Verified 2026-07-29 against a live packer-margin
                // answer whose stored part[0] was text(len=0, provenance=✓)
                // while the on-screen answer showed no chip.
                //
                // Keep empty-content text parts that carry provenance; keep
                // skipping everything else of type "text". Rule lives in
                // src/lib/consultantStreamParts.ts and is locked by tests.
                if (!shouldKeepStreamedPart(part)) break;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === currentMessageId
                      ? { ...m, content: [...m.content, part] }
                      : m,
                  ),
                );
                break;
              }

              case "done": {
                doneReceived = true;
                const finishStatus =
                  (data.status as ConsultantMessage["status"]) ?? "complete";
                // Phase 11b — capture publishable_part_indexes from the
                // done event so the briefing PDF can use it immediately
                // without re-fetching from consultant_messages.
                const indexes = Array.isArray(data.publishable_part_indexes)
                  ? (data.publishable_part_indexes as number[])
                  : null;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === currentMessageId
                      ? { ...m, status: finishStatus, publishable_part_indexes: indexes }
                      : m,
                  ),
                );
                break;
              }

              case "error": {
                errorFromStream = String(data.message ?? "stream error");
                break;
              }
            }
          }
        }

        if (errorFromStream) throw new Error(errorFromStream);
      } catch (err) {
        // ⚠️ A DELIBERATE STOP IS NOT A FAILURE, AND IT IS DETECTED BY TYPE.
        // The transport-drop path below would already have softened this, but
        // only by regex-matching the word "aborted" in an error MESSAGE — text
        // that varies by browser and is one wording change away from rendering
        // "⚠️ The user aborted a request" on top of a perfectly good answer.
        // Whatever streamed before the stop is what the reader chose to keep,
        // so it stays and the message is simply marked complete.
        //
        // ⚠️ THE SERVER DOES NOT STOP. Aborting closes our end of the stream;
        // the edge function runs on and persists the FULL answer, so reloading
        // the conversation later shows more than was on screen when stop was
        // pressed. That is the honest cost of a client-side stop, and it errs
        // the right way: nothing is lost, only deferred.
        if (err instanceof DOMException && err.name === "AbortError") {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === currentMessageId || m.id === streamingPlaceholderId
                ? { ...m, status: "complete" }
                : m,
            ),
          );
          return;
        }
        const msg = (err as Error).message ?? "Something went wrong.";
        // iOS WebKit reports a dropped streaming fetch as "Load failed". When the
        // answer already streamed in, that's a benign stream-CLOSE hiccup, not a
        // real failure — don't deface a complete answer with a scary error
        // footer. Only surface an error when the server sent a real error event,
        // or we genuinely got nothing back.
        const transportDrop =
          !errorFromStream &&
          /load failed|failed to fetch|network|connection|aborted|cancell?ed|terminated/i.test(msg);
        // Only raise a banner for a genuine failure (not a post-answer drop).
        if (!doneReceived && !transportDrop) setLastError(msg);
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== currentMessageId && m.id !== streamingPlaceholderId) {
              return m;
            }
            // The server already signalled completion — the "done" handler
            // finalized this message; a later read error is just the stream
            // closing. Leave the finished answer untouched.
            if (doneReceived) return m;
            const hasContent = m.content.length > 0;
            // Answer already on screen + just a transport drop → finalize clean.
            if (hasContent && transportDrop) {
              return { ...m, status: "complete" };
            }
            return {
              ...m,
              content: hasContent
                ? [...m.content, { type: "text", content: `⚠️ ${msg}` }]
                : [{ type: "text", content: `⚠️ ${msg}` }],
              status: "error",
            };
          }),
        );
      } finally {
        setIsPending(false);
        setRunningTool(null);
        abortRef.current = null;
      }
    },
    [activeConversationId, isPending],
  );

  /** The most recent thing the user actually typed, as plain text. */
  const lastUserText = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== "user") continue;
      const text = m.content
        .map((p) => (p.type === "text" ? p.content : ""))
        .join(" ")
        .trim();
      if (text) return text;
    }
    return "";
  })();

  /**
   * Ask the last question again.
   *
   * ⚠️ IT ASKS AGAIN RATHER THAN REPLACING THE ANSWER, and the question appears
   * in the thread a second time on purpose. The server persists every turn, so
   * quietly swapping the previous answer out of local state would leave the
   * screen disagreeing with the conversation on reload — the same divergence
   * the stop button already has to live with, introduced here for cosmetics
   * rather than necessity. Showing both is what actually happened.
   *
   * ⚠️ AND ANSWERS CAN LEGITIMATELY DIFFER. The consultant reads live tables;
   * asking again after a new print lands SHOULD produce a different number.
   * Presenting a regeneration as a replacement would hide that.
   */
  const regenerate = useCallback(() => {
    if (isPending || !lastUserText) return;
    void sendMessage(lastUserText);
  }, [isPending, lastUserText, sendMessage]);

  /**
   * Halt the answer in flight, keeping whatever has already streamed.
   *
   * Safe to call when nothing is running — the ref is null between sends.
   */
  const stopGeneration = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const loadConversation = useCallback(async (conversationId: string) => {
    setLastError(null);
    const { data, error } = await supabase
      .from("consultant_messages")
      .select("id, conversation_id, role, content, status, created_at, publishable_part_indexes")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });
    if (error) {
      setLastError(error.message);
      return;
    }
    const rows = (data ?? []).map((r) => ({
      id: r.id as string,
      conversation_id: r.conversation_id as string,
      role: r.role as "user" | "assistant",
      content: ((r.content ?? []) as MessagePart[]) ?? [],
      status: (r.status ?? "complete") as ConsultantMessage["status"],
      created_at: r.created_at as string,
    }));
    setActiveConversationId(conversationId);
    setMessages(rows);
  }, []);

  const newConversation = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setLastError(null);
  }, []);

  return (
    <ConsultantContext.Provider
      value={{
        isOpen,
        open,
        close,
        toggle,
        activeConversationId,
        messages,
        isPending,
        runningTool,
        lastError,
        premiumRequired,
        sendMessage,
        stopGeneration,
        regenerate,
        canRegenerate: !isPending && lastUserText !== "",
        loadConversation,
        newConversation,
      }}
    >
      <ProvenanceDrawerProvider>{children}</ProvenanceDrawerProvider>
    </ConsultantContext.Provider>
  );
}

export function useConsultant(): ConsultantContextValue {
  const ctx = useContext(ConsultantContext);
  if (!ctx) {
    throw new Error("useConsultant must be used inside <ConsultantProvider>");
  }
  return ctx;
}
